import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { propertiesAPI } from '../services/api';
import { addView } from '../utils/recentlyViewed';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SourceAttribution from '../components/SourceAttribution';
import { toast } from '../hooks/use-toast';
import PropertyMap from '../components/PropertyMap';
import NeighbourhoodScorecard from '../components/NeighbourhoodScorecard';
import NeighbourhoodVibe from '../components/NeighbourhoodVibe';
import CompareNeighbourhoods from '../components/CompareNeighbourhoods';
import WishlistHeart from '../components/WishlistHeart';
import TotalCostCalculator from '../components/TotalCostCalculator';
import InvestmentAnalysis from '../components/InvestmentAnalysis';
import NegotiationIntelligence from '../components/NegotiationIntelligence';
import VerificationBadges from '../components/VerificationBadges';
import SimilarHomesRail from '../components/SimilarHomesRail';
import HonestNumberPriceDropBadges from '../components/HonestNumberPriceDropBadges';
import EnquiryModal from '../components/EnquiryModal';
import OffMarketInterestButton from '../components/OffMarketInterestButton';
import { useMarket } from '../hooks/useMarket';
import CompareButton from '../components/CompareButton';
import NeighborhoodIntelligence from '../components/NeighborhoodIntelligence';
import ClaimListingModal from '../components/ClaimListingModal';
import LeadGenWidgets from '../components/LeadGenWidgets';
import FloorplanStoryteller from '../components/FloorplanStoryteller';
import HonestReadPanel from '../components/HonestReadPanel';
import HonestyBadge from '../components/HonestyBadge';
import PassportInlinePanel from '../components/PassportInlinePanel';
import PropertyPassportReport from '../components/PropertyPassportReport';
import UkSoldPricesCard from '../components/UkSoldPricesCard';
import PricingCalibrationBadge from '../components/PricingCalibrationBadge';
import PsraPill from '../components/PsraPill';
import RoomFloorplanVisual, { openFloorplanPrint } from '../components/RoomFloorplanVisual';
import NearbySoldPrices from '../components/NearbySoldPrices';
import CountryTrustPill from '../components/CountryTrustPill';
import { localeForCountry, fmtMoney } from '../utils/locale';
import { useAuth } from '../context/AuthContext';
import chatService from '../services/chatService';
import { useNavigate } from 'react-router-dom';
import SEO, { buildPropertyJsonLd, buildBreadcrumbJsonLd } from '../components/SEO';
import {
  Heart,
  Share2,
  Bed,
  Bath,
  Maximize,
  MapPin,
  Phone,
  Mail,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Fire-and-forget click tracker for external listings. Never throws.
 */
const trackExternalClick = (propertyId, channel) => {
  if (!propertyId || !channel) return;
  try {
    axios.post(`${API}/api/properties/${propertyId}/external-click`,
                { channel }, { timeout: 4000 }).catch(() => {});
  } catch (_e) { /* silent */ }
};

const CompareDrawer = ({ property }) => {
  const [open, setOpen] = React.useState(false);
  const areaLabel = [property?.location?.area, property?.location?.city, property?.location?.county]
    .filter(Boolean).join(', ') || property?.location?.fullAddress || 'This area';
  const pickA = {
    label: areaLabel,
    lat:   property.location.lat,
    lng:   property.location.lng,
    locked: true,   // don't let buyers replace side A on the inline drawer
  };
  return (
    <div className="mb-4" data-testid="property-compare-drawer">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5b35a8] hover:underline"
        data-testid="property-compare-toggle"
        aria-expanded={open}
      >
        {open ? '× Close comparison' : 'Compare this area with…'}
      </button>
      {open && (
        <div className="mt-3 rounded-lg ring-1 ring-gray-200 bg-gray-50/60 p-3">
          <CompareNeighbourhoods
            variant="inline"
            initialA={pickA}
            currentPropertyId={property.id || property._id}
          />
        </div>
      )}
    </div>
  );
};

const PropertyDetailPage = ({ countryOverride } = {}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const market = useMarket(countryOverride);
  const [startingChat, setStartingChat] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const startChat = async (propertyId) => {
    if (!user) {
      toast({ title: 'Please sign in first',
              description: 'Create a free account to message the agent.' });
      navigate('/login?next=' + encodeURIComponent(window.location.pathname));
      return;
    }
    setStartingChat(true);
    try {
      const r = await chatService.createOrGet(propertyId);
      navigate(`/inbox/${r.chat.id}`);
    } catch (e) {
      toast({ title: 'Could not open chat',
              description: e?.response?.data?.detail || '',
              variant: 'destructive' });
    } finally { setStartingChat(false); }
  };

  const { id } = useParams();
  const { isAuthenticated } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const data = await propertiesAPI.getById(id);
        setProperty(data);
        // Track in localStorage for the "Recently viewed" rail
        addView(data);
        // Increment view count
        await propertiesAPI.incrementView(id);
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="p-12 text-center">
          <p className="text-gray-600">Loading property...</p>
        </Card>
      </div>
    );
  }

  if (!property) {
    // Soft-404 fix: this is a React SPA so the HTTP response was already
    // 200 by the time we know the listing is missing. The next-best signal
    // to Google is a JS-rendered <meta name="robots" content="noindex">
    // plus a 410-gone-style noarchive hint. Google's modern renderer
    // honours these and will drop the URL from the index on next crawl.
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex,nofollow,noarchive" />
          <meta name="googlebot" content="noindex,nofollow" />
          <title>Property not found · lovinahome</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="p-12 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Not Found</h2>
            <p className="text-gray-600 mb-6">
              This property is no longer listed. It may have been sold, withdrawn, or never existed.
            </p>
            <Link to={market.withPrefix('/listings')}>
              <Button>Browse Properties</Button>
            </Link>
          </Card>
        </div>
      </>
    );
  }

  // Locale-aware (UK = £/GBP/EPC, IE = €/EUR/BER). Drives every visible
  // price, energy badge and postcode label on this page.
  const L = localeForCountry(property?.location?.country);

  const formatPrice = (price) => fmtMoney(price, property?.location?.country);

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === property.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? property.images.length - 1 : prev - 1
    );
  };

  const handleSave = () => {
    setIsSaved(!isSaved);
    toast({
      title: isSaved ? 'Removed from saved' : 'Saved successfully',
      description: isSaved
        ? 'Property removed from your saved list'
        : 'Property added to your saved list',
    });
  };

  const handleShare = async () => {
    // Use the bot-friendly /api/og/property/{id} URL so WhatsApp, iMessage,
    // Twitter, Slack etc. show a rich preview (cover photo + price + title).
    // Humans get instantly redirected to the canonical /property/{id} route.
    const pid = property._id || property.id;
    const apiBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const shareUrl = `${apiBase}/api/og/property/${pid}`;
    const shareData = {
      title: property.title,
      text: `${property.title} — ${L.currencySymbol}${Number(property.price || 0).toLocaleString()}`,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (_e) {
      // user cancelled or share unsupported — fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Share link copied!',
        description: 'Paste it anywhere — chat apps will show a rich preview.',
      });
    } catch (_e) {
      toast({
        title: 'Could not copy link',
        description: shareUrl,
      });
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-24 lg:pb-0">
      <SEO
        title={property.title}
        description={(property.description || '').slice(0, 200)}
        image={`${process.env.REACT_APP_BACKEND_URL || ''}/api/og/property/${property._id || property.id}.png`}
        path={`/property/${property._id || property.id}`}
        type="article"
        country={property.location?.country}
        jsonLd={[
          buildPropertyJsonLd(property),
          buildBreadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Listings', path: '/listings' },
            { name: property.title, path: `/property/${property._id || property.id}` },
          ]),
        ]}
      />
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to={market.withPrefix('/buy')} data-testid="back-to-listings">
            <Button variant="ghost">
              <ChevronLeft className="h-5 w-5 mr-2" />
              Back to Listings
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card className="overflow-hidden">
              <div className="relative h-96 bg-gray-900">
                <img
                  src={property.images[currentImageIndex]}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
                {/* Sold / Sale-Agreed diagonal ribbon — keeps the listing
                    visible for buyer trust ("this platform closes deals")
                    while making the transacted state unmistakable. */}
                {property.status && ['sold', 'sale_agreed'].includes((property.status || '').toLowerCase()) && (
                  <>
                    <div
                      className="absolute inset-0 bg-black/40 pointer-events-none"
                      data-testid={`detail-status-overlay-${property.status}`}
                    />
                    <div
                      className={`absolute top-10 -right-16 w-64 text-center py-2 rotate-45 shadow-lg text-white text-sm font-extrabold tracking-widest uppercase pointer-events-none ${
                        property.status === 'sold' ? 'bg-rose-600' : 'bg-amber-500'
                      }`}
                      data-testid={`detail-status-ribbon-${property.status}`}
                    >
                      {property.status === 'sold' ? 'Sold' : 'Sale Agreed'}
                    </div>
                  </>
                )}
                {property.images.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white"
                      onClick={prevImage}
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white"
                      onClick={nextImage}
                    >
                      <ChevronRight className="h-6 w-6" />
                    </Button>
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {property.images.length}
                    </div>
                  </>
                )}
              </div>
              {/* Thumbnail Gallery */}
              {property.images.length > 1 && (
                <div className="grid grid-cols-6 gap-2 p-4">
                  {property.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`aspect-video rounded overflow-hidden ${
                        currentImageIndex === index ? 'ring-2 ring-[#7042cc]' : ''
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {/* Property Details */}
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Header */}
                <div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-start sm:justify-between gap-3 mb-4">
                    <div className="min-w-0 flex-1 order-2 sm:order-1">
                      {property.location?.lat != null && property.location?.lng != null && (
                        <NeighbourhoodVibe
                          lat={property.location.lat}
                          lng={property.location.lng}
                        />
                      )}
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        {property.title}
                      </h1>
                      <div className="flex items-center text-gray-600 mb-2">
                        <MapPin className="h-5 w-5 mr-2" />
                        <span>{property.location.fullAddress}</span>
                      </div>
                      {property.location?.lat != null && property.location?.lng != null && (
                        <div className="mb-3 flex items-center gap-2" data-testid="property-scorecard-header">
                          <div className="flex-1">
                            <NeighbourhoodScorecard
                              lat={property.location.lat}
                              lng={property.location.lng}
                              variant="detail"
                            />
                          </div>
                          <WishlistHeart
                            lat={property.location.lat}
                            lng={property.location.lng}
                            label={[property.location.area, property.location.city, property.location.county]
                              .filter(Boolean).join(', ') || property.location.fullAddress || 'Area'}
                            size="lg"
                          />
                        </div>
                      )}
                      {property.location?.lat != null && property.location?.lng != null && (
                        <CompareDrawer
                          property={property}
                        />
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {(() => {
                          const raw = property.createdAt || property.dateAdded;
                          const d = raw ? new Date(raw) : null;
                          if (!d || Number.isNaN(d.getTime())) return null;
                          return (
                            <div className="flex items-center" data-testid="property-added-date">
                              <Calendar className="h-4 w-4 mr-1" />
                              Added {d.toLocaleDateString('en-IE')}
                            </div>
                          );
                        })()}
                        <div>{property.views} views</div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0 order-1 sm:order-2 self-end sm:self-start">
                      <Button variant="outline" size="icon" onClick={handleSave} data-testid="property-save-heart">
                        <Heart
                          className={`h-5 w-5 ${
                            isSaved ? 'fill-red-500 text-red-500' : ''
                          }`}
                        />
                      </Button>
                      <Button variant="outline" size="icon" onClick={handleShare} data-testid="property-share-btn">
                        <Share2 className="h-5 w-5" />
                      </Button>
                      <CompareButton propertyId={property._id || property.id} variant="inline" />
                    </div>
                  </div>

                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-[#7042cc]">
                      {formatPrice(property.price)}
                    </span>
                    {property.type === 'rent' && (
                      <span className="text-gray-600">/month</span>
                    )}
                  </div>
                  <HonestNumberPriceDropBadges
                    propertyId={property._id || property.id}
                    propertyType={property.type}
                    country={property.location?.country}
                  />
                </div>

                {/* Key Features */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-y border-gray-200">
                  <div className="text-center">
                    <Bed className="h-8 w-8 mx-auto mb-2 text-[#7042cc]" />
                    <div className="font-semibold">{property.bedrooms}</div>
                    <div className="text-sm text-gray-600">Bedrooms</div>
                  </div>
                  <div className="text-center">
                    <Bath className="h-8 w-8 mx-auto mb-2 text-[#7042cc]" />
                    <div className="font-semibold">{property.bathrooms}</div>
                    <div className="text-sm text-gray-600">Bathrooms</div>
                  </div>
                  <div className="text-center">
                    <Maximize className="h-8 w-8 mx-auto mb-2 text-[#7042cc]" />
                    <div className="font-semibold">{property.size} m²</div>
                    <div className="text-sm text-gray-600">Size</div>
                  </div>
                  <div className="text-center">
                    <Badge className="text-lg px-4 py-2">{L.energyLabel} {property.ber}</Badge>
                    <div className="text-sm text-gray-600 mt-2">Energy Rating</div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Description</h2>
                  <p className="text-gray-700 leading-relaxed">{property.description}</p>
                </div>

                {/* Features */}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Features</h2>
                  <div className="flex flex-wrap gap-2">
                    {property.features.map((feature, index) => (
                      <Badge key={index} variant="secondary" className="text-sm">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Room Sizes — floor-plan style, proportional to metres.
                    Rendered for any listing where the scraper (or admin)
                    has populated a structured room breakdown. */}
                {Array.isArray(property.rooms) && property.rooms.length > 0 && (
                  <div data-testid="property-room-sizes">
                    <h2 className="text-xl font-bold text-gray-900 mb-3">Room Sizes</h2>
                    <RoomFloorplanVisual
                      rooms={property.rooms}
                      showUnitToggle
                      defaultUnit={
                        ['United States', 'USA', 'US'].includes(property.location?.country)
                          ? 'ft' : 'm'
                      }
                      onPrint={() => openFloorplanPrint({ property })}
                    />
                  </div>
                )}

                {/* Location Map */}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Location</h2>
                  <div className="mb-3 flex items-start">
                    <MapPin className="h-5 w-5 mr-2 text-[#7042cc] mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700">{property.location.fullAddress}</p>
                  </div>
                  <PropertyMap
                    property={property}
                    location={property.location}
                    height="420px"
                    showNearby
                    showAmenities
                    showCommute
                    showNeighbourhoodAI
                    showSoldPrices
                    propertyId={property._id || property.id}
                  />
                  <NearbySoldPrices property={property} />
                </div>
              </CardContent>
            </Card>

            {/* UK only — HM Land Registry sold-price comparables for the outward postcode */}
            {(property.location?.country === 'United Kingdom' || property.location?.country === 'UK') &&
              property.location?.postcode && (
                <UkSoldPricesCard postcode={property.location.postcode} limit={5} />
              )}

            {/* Property Passport Report — 6-section auto-populated, under
                the photos. Market-aware via passport_section_uk for UK
                listings, IE builders for everyone else. Each section
                emits a currency/locale envelope so the same React renders
                both. */}
            <PropertyPassportReport
              propertyId={property._id || property.id}
              country={property.location?.country}
              property={property}
            />


            {/* Professional Property Intelligence Suite */}
            <VerificationBadges property={property} />

            {/* Honest Pricing Calibration — admin preview unless flag is on */}
            <PricingCalibrationBadge
              propertyId={property._id || property.id}
              property={property}
            />

            {/* Owner's Logbook — public read, owner/agent/admin write, anyone can report.
                Market-aware: BER label on IE listings, EPC on UK, Energy Score on USA. */}
            <PassportInlinePanel
              propertyId={property._id || property.id}
              country={property.location?.country}
            />

            <NeighborhoodIntelligence property={property} />
            {property.type !== 'rent' && (
              <>
                <HonestReadPanel
                  propertyId={property._id || property.id}
                  country={property.location?.country}
                />
                <FloorplanStoryteller property={property} />
                <TotalCostCalculator
                  askingPrice={property.price}
                  country={property.location?.country}
                />
                <InvestmentAnalysis property={property} />
                <NegotiationIntelligence property={property} />
                <LeadGenWidgets property={property} />
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Agent Contact Card */}
            <Card className="sticky top-20">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Agent</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-gray-900">{property.agent.name}</p>
                    <div className="mt-1">
                      <CountryTrustPill propertyId={property._id || property.id} />
                    </div>
                    {property.is_external && (
                      <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1" data-testid="external-listing-badge">
                        <ExternalLink className="h-3 w-3" />
                        Listed by {property.external_agent?.agency || property.agent.name} on
                        {' '}<a href={property.source_url}
                                target="_blank" rel="noreferrer"
                                onClick={() => trackExternalClick(property._id || property.id, 'source_url')}
                                className="text-[#5b35a8] hover:underline"
                                data-testid="external-source-link">
                          their site
                        </a>
                      </p>
                    )}
                  </div>
                  {property.userId && !property.is_external ? (
                    <HonestyBadge userId={property.userId} />
                  ) : null}
                  {!property.is_external && (
                    <Button className="w-full bg-[#7042cc] hover:bg-[#5b35a8]"
                            onClick={() => startChat(property._id || property.id)}
                            disabled={startingChat}
                            data-testid="property-message-agent">
                      <MessageSquare className="h-5 w-5 mr-2" />
                      {startingChat ? 'Opening…' : 'Message agent'}
                    </Button>
                  )}
                  {property.agent.phone && (
                    <Button variant="outline" className="w-full" asChild>
                      <a href={`tel:${property.agent.phone}`}
                          onClick={() => trackExternalClick(property._id || property.id, 'phone')}
                          data-testid="property-contact-phone">
                        <Phone className="h-5 w-5 mr-2" />
                        {property.agent.phone}
                      </a>
                    </Button>
                  )}
                  {property.agent.email && (
                    <Button variant="outline" className="w-full" asChild>
                      <a href={`mailto:${property.agent.email}?subject=Enquiry%20about%20${encodeURIComponent(property.title)}%20(via%20lovinahome)`}
                          onClick={() => trackExternalClick(property._id || property.id, 'email')}
                          data-testid="property-contact-email">
                        <Mail className="h-5 w-5 mr-2" />
                        Email agent
                      </a>
                    </Button>
                  )}
                  <OffMarketInterestButton propertyId={property._id || property.id} />
                  {property.is_external && property.external_agent?.website && (
                    <Button variant="outline" className="w-full" asChild>
                      <a href={property.external_agent.website}
                          target="_blank" rel="noreferrer"
                          onClick={() => trackExternalClick(property._id || property.id, 'website')}
                          data-testid="property-contact-website">
                        <ExternalLink className="h-5 w-5 mr-2" />
                        Visit agency website
                      </a>
                    </Button>
                  )}
                  {property.is_external && (
                    <div className="pt-3 border-t border-gray-200 text-[11px] text-gray-500">
                      You&apos;re being connected via <strong>lovinahome</strong> — when you reach out, please
                      let {property.external_agent?.agency || property.agent.name} know you found this here.
                    </div>
                  )}
                  {property.is_external && (
                    <div className="pt-3 border-t border-gray-200">
                      <SourceAttribution
                        sourceUrl={property.source_url}
                        claimed={!!property.claimed_by_user_id}
                        variant="detail"
                        onClaim={() => setClaimOpen(true)}
                      />
                      <p className="text-[10px] text-gray-400 mt-2" data-testid="external-compliance-footer">
                        Reproduced for buyer discovery. Original by{' '}
                        <strong>{property.external_agent?.agency || property.agent?.name}</strong>.
                      </p>
                    </div>
                  )}
                  {!property.is_external && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600 mb-3">
                        Interested in this property? Send a message to the agent.
                      </p>
                      <Button variant="outline" className="w-full">
                        Request Viewing
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Property Type Badge */}
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  {property.type === 'rent' && (
                    <Badge className="text-lg px-6 py-2 bg-green-600">To Rent</Badge>
                  )}
                  {property.type === 'sale' && (
                    <Badge className="text-lg px-6 py-2 bg-[#7042cc]">For Sale</Badge>
                  )}
                  <p className="text-sm text-gray-600 mt-3 capitalize">
                    {property.propertyType}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Personalised similar-homes rail at the bottom */}
      <SimilarHomesRail propertyId={property?._id} />

      <ClaimListingModal
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        property={property}
      />

      {/* Sticky bottom Contact bar — mobile only. Sits above the
          browser chrome so buyers can enquire without scrolling past
          gallery, features and neighbourhood copy to reach the
          desktop sidebar. */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200
                    bg-white/95 backdrop-blur px-3 py-2 flex items-center gap-2 shadow-lg"
        data-testid="mobile-contact-bar"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
            {property.is_external ? 'Listed by' : 'Agent'}
          </div>
          <div className="text-xs font-semibold text-gray-900 truncate">
            {property.agent?.name || 'Contact'}
          </div>
        </div>
        {property.agent?.phone && (
          <a
            href={`tel:${property.agent.phone}`}
            onClick={() => trackExternalClick(property._id || property.id, 'phone')}
            data-testid="mobile-contact-phone"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full
                        bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
            aria-label="Call agent"
          >
            <Phone className="h-4 w-4" />
          </a>
        )}
        {/* Email-style enquiry fallback for buyers who don't want to
            spin up a chat session — opens the modal on tap. */}
        <button
          type="button"
          onClick={() => setEnquiryOpen(true)}
          data-testid="mobile-contact-enquire"
          className="inline-flex items-center justify-center h-10 w-10 rounded-full
                      bg-white text-[#7042cc] ring-1 ring-[#7042cc]/30 hover:bg-[#7042cc]/10 shadow-sm"
          aria-label="Send an email enquiry"
        >
          <Mail className="h-4 w-4" />
        </button>
        {property.is_external ? (
          <a
            href={property.source_url}
            target="_blank" rel="noreferrer"
            onClick={() => trackExternalClick(property._id || property.id, 'source_url')}
            data-testid="mobile-contact-external"
            className="flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full
                        bg-[#7042cc] hover:bg-[#5b35a8] text-white text-sm font-semibold shadow-sm"
          >
            <ExternalLink className="h-4 w-4" />
            Contact on source
          </a>
        ) : (
          <Button
            onClick={() => startChat(property._id || property.id)}
            disabled={startingChat}
            data-testid="mobile-contact-message"
            className="flex-shrink-0 h-10 px-4 rounded-full bg-[#7042cc] hover:bg-[#5b35a8]
                        text-white text-sm font-semibold shadow-sm"
          >
            <MessageSquare className="h-4 w-4 mr-1.5" />
            {startingChat ? 'Opening…' : 'Message agent'}
          </Button>
        )}
      </div>
      <EnquiryModal
        open={enquiryOpen}
        onOpenChange={setEnquiryOpen}
        property={property}
      />
    </div>
  );
};

export default PropertyDetailPage;
